"use client";

import { useState } from "react";
import { useAppRouter as useRouter } from "@/lib/navigation";
import {
  CamundaProcessService,
  CamundaTaskService,
  CamundaUserService,
  type ProcessDefinition,
} from "@/lib/camunda";
import { ProcessCategory } from "@/components/process-category";
import { LoadingStates } from "@/components/loading-states";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { InfoIcon } from "lucide-react";
import { useCanCreateProcess } from "@/hooks/usePermissions";
import { useAuthorizedProcesses } from "@/hooks/use-authorized-processes";

export default function StartProcessPage() {
  const router = useRouter();
  const [startingProcess, setStartingProcess] = useState<string | null>(null);
  
  const { loading: permissionLoading } = useCanCreateProcess();
  const {
    categorizedProcesses,
    loading,
    error,
    reload
  } = useAuthorizedProcesses(!permissionLoading);

  const handleStartProcess = async (process: ProcessDefinition) => {
    setStartingProcess(process.key);
    try {
      const instance = await CamundaProcessService.startProcess(process.key);
      const currentUserId = CamundaUserService.getCurrentUserId();
      const workableTasks = currentUserId && instance?.id
        ? await CamundaTaskService.getUserParticipatingRuntimeTasksForProcess(
            instance.id,
            currentUserId,
          )
        : [];
      const firstTask = workableTasks[0];

      if (firstTask?.id) {
        router.push(`/task-list/tasks/${firstTask.id}`);
      } else {
        alert(`Proceso "${process.name}" iniciado exitosamente`);
      }
    } catch (err: unknown) {
      console.error('Error starting process:', err);
      const message = err instanceof Error ? err.message : 'Error desconocido';
      alert(`Error al iniciar el proceso: ${message}`);
    }
    setStartingProcess(null);
  };

  const hasProcesses = Object.keys(categorizedProcesses).length > 0 && 
    Object.values(categorizedProcesses).some(processes => processes.length > 0);

  return (
    <div className="h-full p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Iniciar Proceso</h1>
        <p className="text-gray-600">Selecciona un proceso para iniciar una nueva instancia</p>
      </div>

      {(loading || permissionLoading) && (
        <LoadingStates loading={true} />
      )}

      {error && (
        <LoadingStates 
          error="Error al cargar los procesos. Verifica tu conexión."
          onRetry={reload}
        />
      )}

      {!loading && !permissionLoading && !error && !hasProcesses && (
        <Alert className="max-w-2xl mx-auto mt-8">
          <InfoIcon className="h-4 w-4" />
          <AlertDescription className="text-center">
            No tienes asignado ningún proceso para iniciar contacta con un administrador si debes iniciar algún proceso.
          </AlertDescription>
        </Alert>
      )}

      {!loading && !permissionLoading && !error && hasProcesses && (
        <div className="space-y-4">
          {Object.entries(categorizedProcesses).map(([category, processes]) => (
            <ProcessCategory
              key={category}
              category={category}
              processes={processes}
              startingProcess={startingProcess}
              onStartProcess={handleStartProcess}
            />
          ))}
        </div>
      )}
    </div>
  );
}
